import type { AppModel } from "@jti/user-auth";

import { mapAgeVerificationMethodImages, mapDeviceCodeLengths } from "@/libs/builder/common/reactAppsAuthoringMapping";

import { DEFAULT_STEPPER_PROGRESS_LABEL } from "./constants";
import type { UserAuthProps } from "./UserAuth";

const singleConsentValueMap: Record<string, "true" | "false" | undefined> = {
  true: "true",
  false: "false",
  blank: undefined,
};

export const mapUserAuthOptionsToAppModel = ({
  consents,
  deviceRegistration,
  emailValidation,
  offlineRegistration,
  commonLoginSettings,
  phoneValidationOtp,
  genderInformation,
  registrationCommon,
  registrationSuccessPage,
  loyaltyCardSuccessPage,
  ageVerificationMethodImages,
  uxRevamp,
}: UserAuthProps): AppModel => {
  const deviceCodeLength = mapDeviceCodeLengths(deviceRegistration?.deviceCodeLength);

  const uxRevampProps: AppModel["uxRevamp"] = {
    authEntryInformationalText: uxRevamp?.authEntryInformationalText || "",
    authHighlights: (uxRevamp?.authHighlights || []).reduce<string[]>((acc, h) => {
      if (h.value) {
        acc.push(h.value);
      }
      return acc;
    }, []),
    isLoginRevampEnabled: !!uxRevamp?.isLoginRevampEnabled,
    isOtpSendAuto: !!uxRevamp?.isOtpSendAuto,
    defaultCountryCode: uxRevamp?.defaultCountryCode,
  };

  return {
    ageVerificationMethodImages: mapAgeVerificationMethodImages(ageVerificationMethodImages),
    consents: {
      ...consents,
      oneTrustErrorRedirectUrl: consents?.oneTrustErrorRedirectUrl || "",
      oneTrustErrorLinkText: consents?.oneTrustErrorLinkText || "",
      oneTrustErrorMessage: consents?.oneTrustErrorMessage || "",
      legalConditionsForConsents: consents?.legalConditionsForConsents || "",
      singleOptionConsentDefaultValue: singleConsentValueMap[consents?.singleOptionConsentDefaultValue ?? "blank"],
    },
    offlineRegistration: offlineRegistration
      ? {
          isAvEnabled: !!offlineRegistration.isAvEnabled,
          isDeviceRegistrationEnabled: !!offlineRegistration.isDeviceRegistrationEnabled,
        }
      : undefined,
    deviceRegistration: deviceRegistration
      ? {
          ...deviceRegistration,
          isScanDeviceQrEnabled: !!deviceRegistration?.isScanDeviceQrEnabled,
          deviceCodeLength,
          isDeviceRegistrationByLeafletEnabled: !!deviceRegistration?.isDeviceRegistrationByLeafletEnabled,
          isRegistrationLabelEnabled: !!deviceRegistration?.isRegistrationLabelEnabled,
          isOverrideHelperRegistrationText: !!deviceRegistration?.isOverrideHelperRegistrationText,
        }
      : undefined,
    emailValidation: emailValidation
      ? {
          ...emailValidation,
          isOtpNumeric: !!emailValidation.isOtpNumeric,
        }
      : undefined,
    commonLoginSettings: commonLoginSettings
      ? {
          ...commonLoginSettings,
          socialLoginSeparatorText: commonLoginSettings?.socialLoginSeparatorText || "",
        }
      : undefined,
    phoneValidationOtp: phoneValidationOtp
      ? {
          ...phoneValidationOtp,
          failedValidationViaSmsMessage: phoneValidationOtp?.failedValidationViaSmsMessage || "",
          isOtpNumeric: !!phoneValidationOtp?.isOtpNumeric,
          successValidationViaSmsMessage: phoneValidationOtp?.successValidationViaSmsMessage || "",
        }
      : undefined,
    genderInformation: {
      isGenderEnabled: !!genderInformation?.isGenderEnabled,
    },
    registrationCommon: registrationCommon
      ? {
          ...registrationCommon,
          avDisclaimerMessage: registrationCommon.avDisclaimerMessage || "",
          isAgeVerificationUuidFromCookieRequired: !!registrationCommon.isAgeVerificationUuidFromCookieRequired,
          isAutocompleteDisabled: !!registrationCommon.isAutocompleteDisabled,
          isPostCodeNotRequired: !!registrationCommon.isPostCodeNotRequired,
          isRegistrationClosableBeforeAv: !!registrationCommon.isRegistrationClosableBeforeAv,
          isAdditionalPhoneValidationEnabled: !!registrationCommon.isAdditionalPhoneValidationEnabled,
          progressLabel: registrationCommon.progressLabel || DEFAULT_STEPPER_PROGRESS_LABEL,
        }
      : undefined,
    registrationSuccessPage: registrationSuccessPage
      ? {
          ...registrationSuccessPage,
          disclaimer: registrationSuccessPage.disclaimer || "",
          hidePloomClubBanner: !!registrationSuccessPage.hidePloomClubBanner,
          learnMoreLinkText: registrationSuccessPage.learnMoreLinkText || "",
          learnMoreLinkUrl: registrationSuccessPage.learnMoreLinkUrl || "",
          successBannerMessage: registrationSuccessPage.successBannerMessage || "",
        }
      : undefined,
    loyaltyCardSuccessPage: loyaltyCardSuccessPage
      ? {
          isLoyaltyCardSuccessPageEnabled: !!loyaltyCardSuccessPage.isLoyaltyCardSuccessPageEnabled,
          isPloomBenefitsBannerHidden: !!loyaltyCardSuccessPage.isPloomBenefitsBannerHidden,
          clubCardLogo: loyaltyCardSuccessPage.clubCardLogo || "",
        }
      : undefined,
    uxRevamp: uxRevampProps,
  };
};
